import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, User, ShieldCheck, KeyRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PermissionsMatrix from "@/components/PermissionsMatrix";

const ROLES = [
  "super_admin", "company_admin", "department_manager",
  "noc_engineer", "billing_executive", "fraud_analyst",
  "support_agent", "read_only_auditor", "ai_approver",
];

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", password: "", full_name: "", role: "support_agent", company_ids: [] });
  const [permTarget, setPermTarget] = useState(null);
  const [credTarget, setCredTarget] = useState(null); // { id, username, newUsername, newPassword }

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!credTarget) return;
    const patch = {};
    const nu = (credTarget.newUsername || "").trim();
    const np = credTarget.newPassword || "";
    if (nu && nu !== credTarget.username) patch.username = nu;
    if (np) patch.password = np;
    if (Object.keys(patch).length === 0) {
      toast.error("Nothing to change");
      return;
    }
    try {
      await api.patch(`/users/${credTarget.id}`, patch);
      toast.success("Credentials updated");
      setCredTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
    setUsers(u.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, username: form.username.toLowerCase() });
      toast.success("User created");
      setOpen(false);
      setForm({ username: "", email: "", password: "", full_name: "", role: "support_agent", company_ids: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="users-page">
      <PageHeader
        title="Users & roles"
        subtitle="Assign roles and control which companies and departments a user can access."
        actions={
          user?.role === "super_admin" || user?.role === "company_admin" ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="new-user-button"><Plus className="h-4 w-4 mr-1.5" /> New user</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Create user</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Username"><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="user-username-input" /></F>
                    <F label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" /></F>
                  </div>
                  <F label="Full name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname-input" /></F>
                  <F label="Password"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" /></F>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Role">
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </F>
                    <F label="Company">
                      <Select value={form.company_ids[0] || ""} onValueChange={(v) => setForm({ ...form, company_ids: v ? [v] : [] })}>
                        <SelectTrigger className="rounded-sm" data-testid="user-company-select"><SelectValue placeholder="All" /></SelectTrigger>
                        <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </F>
                  </div>
                  <DialogFooter><Button type="submit" className="rounded-sm" data-testid="user-create-submit">Create</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />
      <div className="p-6 lg:p-8">
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="users-table">
            <thead><tr><th>User</th><th>Role</th><th>Companies</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-sm bg-terracotta/15 text-terracotta grid place-items-center"><User className="h-3.5 w-3.5" /></div>
                      <div>
                        <div className="font-medium">{u.full_name || u.username}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td><Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{u.role.replace(/_/g, " ")}</Badge></td>
                  <td className="text-xs font-mono text-muted-foreground">{u.company_ids?.length || "all"}</td>
                  <td>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${u.active ? "bg-emerald-deep/15 text-emerald-deep" : "bg-muted text-muted-foreground"}`}>{u.active ? "yes" : "no"}</span>
                  </td>
                  <td className="text-right">
                    {(user?.role === "super_admin" || user?.role === "company_admin") && (
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setPermTarget(u)} data-testid={`edit-perms-${u.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Permissions
                      </Button>
                    )}
                    {user?.role === "super_admin" && (
                      <Button size="sm" variant="outline" className="rounded-sm text-xs ml-1" onClick={() => setCredTarget({ id: u.id, username: u.username, newUsername: u.username, newPassword: "" })} data-testid={`edit-creds-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Credentials
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">No users.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <PermissionsMatrix open={!!permTarget} onClose={() => setPermTarget(null)} user={permTarget} onSaved={load} />

      <Dialog open={!!credTarget} onOpenChange={(o) => !o && setCredTarget(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="edit-credentials-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2"><KeyRound className="h-5 w-5 text-emerald-brand" /> Edit credentials</DialogTitle>
            <p className="text-xs text-muted-foreground">Super admin can rename this user and issue a new password. Leave a field empty to keep it unchanged.</p>
          </DialogHeader>
          {credTarget && (
            <form onSubmit={submitCreds} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Current username</Label>
                <Input value={credTarget.username} disabled className="rounded-xl bg-muted/40" data-testid="creds-current-username" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New username</Label>
                <Input value={credTarget.newUsername} onChange={(e) => setCredTarget({ ...credTarget, newUsername: e.target.value })} className="rounded-xl" data-testid="creds-new-username" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New password (min 6 chars)</Label>
                <Input type="password" value={credTarget.newPassword} onChange={(e) => setCredTarget({ ...credTarget, newPassword: e.target.value })} className="rounded-xl" data-testid="creds-new-password" placeholder="Leave blank to keep current" />
              </div>
              <DialogFooter>
                <Button type="submit" className="rounded-xl" data-testid="creds-submit">Save changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
